
/*
 * VNCmail : A whole new experience in enterprise email communication.
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { OnInit, OnDestroy, Component, Inject } from "@angular/core";
import { NgxHotkeysService } from "ngx-hotkeys-vnc";
import { MailFolder } from "../../../mail/models/mail-folder.model";
import { NestedTreeControl } from "@angular/cdk/tree";
import { TranslateService } from "@ngx-translate/core";
import { take } from "rxjs/operators";
import { MatTreeNestedDataSource } from "@angular/material/tree";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { FormControl } from "@angular/forms";
import { AppService } from "src/app/services/app.service";

@Component({
    selector: "vp-all-folders",
    templateUrl: "./all-folders.component.html"
})
export class AllFoldersDialogComponent implements OnInit, OnDestroy {
    searchControl: FormControl = new FormControl("", []);
    currentTheme = localStorage.getItem("theme");
    allFolders: MailFolder[] = [];
    messageFolders: MailFolder[] = [];
    documentFolders: MailFolder[] = [];
    contactFolders: MailFolder[] = [];
    taskFolders: MailFolder[] = [];
    otherFolders: MailFolder[] = [];
    calendarFolders: MailFolder[] = [];
    translation: any;
    treeControl = new NestedTreeControl<MailFolder>(node => node.children);
    dataSource = new MatTreeNestedDataSource<MailFolder>();
    selectedMailFolder: any;
    selectedFolder: any;
    isFluid: boolean = false;
    hasChild = (_: number, node: MailFolder) => !!node.children && node.children.length > 0;
    constructor(
        private dialogRef: MatDialogRef<AllFoldersDialogComponent>,
        private ngxHotKeyService: NgxHotkeysService,
        @Inject(MAT_DIALOG_DATA) private data: any,
        private translateService: TranslateService,
        private appService: AppService,
    ) {
        this.isFluid = this.appService.isInsideIframe();
        this.dialogRef.disableClose = true;
        this.translateService.get([
            "CONATACT_LISTS_LBL",
            "CALENDARS_LBL",
            "TASK_LISTS_LBL",
            "BRIEFCASE_FOLDERS_LBL",
            "MAIL_FOLDERS_LBL"
        ]).pipe(take(1)).subscribe(res => {
            this.translation = res;
        });
    }

    ngOnInit() {
        this.ngxHotKeyService.pause(this.ngxHotKeyService.hotkeys);
        if (this.data?.filterDialog) {
            this.allFolders = this.generateMailTree();
        } else {
            this.allFolders = this.generateTree();
        }
        this.dataSource.data = this.allFolders;
        this.treeControl.dataNodes = this.allFolders;
        this.treeControl.expandAll();
    }

    ngOnDestroy() {
        this.ngxHotKeyService.unpause(this.ngxHotKeyService.hotkeys);
    }

    close(): void {
        this.dialogRef.close();
    }

    folderItem(folder: MailFolder): void {
        if (folder.id !== "") {
            this.selectedFolder = folder;
        }
    }

    save() {
        if (this.selectedFolder.id !== "") {
            this.dialogRef.close({ selectedFolder: this.selectedFolder });
        }
    }

    private generateTree(): MailFolder[] {
        let allFolders: MailFolder[] = [];
        this.messageFolders = this.data.allFolder.filter(folder => folder.view === "message").filter( f => f.absFolderPath !== "/Chats");
        this.documentFolders = this.data.allFolder.filter(folder => folder.view === "document");
        this.contactFolders = this.data.allFolder.filter(folder => folder.view === "contact");
        this.taskFolders = this.data.allFolder.filter(folder => folder.view === "task");
        this.calendarFolders = this.data.allFolder.filter(folder => folder.view === "appointment");
        this.otherFolders = this.data.allFolder.filter(folder => !folder.view && folder.id !== "3");
        if (this.messageFolders.length > 0) {
            this.messageFolders = [
                {
                    name: this.translation.MAIL_FOLDERS_LBL,
                    rgb: "#f8f8f8",
                    children: this.messageFolders,
                    id: "",
                    originalFolder: ""
                }
            ];
        }
        if (this.documentFolders.length > 0) {
            this.documentFolders = [
                {
                    name: this.translation.BRIEFCASE_FOLDERS_LBL,
                    rgb: "#f8f8f8",
                    children: this.documentFolders,
                    id: "",
                    originalFolder: ""
                }
            ];
        }
        if (this.contactFolders.length > 0) {
            this.contactFolders = [
                {
                    name: this.translation.CONATACT_LISTS_LBL,
                    rgb: "#f8f8f8",
                    children: this.contactFolders,
                    id: "",
                    originalFolder: ""
                }
            ];
        }
        if (this.taskFolders.length > 0) {
            this.taskFolders = [
                {
                    name: this.translation.TASK_LISTS_LBL,
                    rgb: "#f8f8f8",
                    children: this.taskFolders,
                    id: "",
                    originalFolder: ""
                }
            ];
        }
        if (this.calendarFolders.length > 0) {
            this.calendarFolders = [
                {
                    name: this.translation.CALENDARS_LBL,
                    rgb: "#f8f8f8",
                    children: this.calendarFolders,
                    id: "",
                    originalFolder: ""
                }
            ];
        }
        if (this.data.extension) {
            const extension = this.data.extension;
            if (extension === "csv" || extension === "vcf") {
                allFolders = [
                    ...this.contactFolders
                ];
            } else if (extension === "tgz" || extension === "zip" || extension === "tar") {
                allFolders = [
                    ...this.messageFolders,
                    ...this.documentFolders,
                    ...this.calendarFolders,
                    ...this.taskFolders,
                    ...this.contactFolders,
                    ...this.otherFolders
                ];
            } else if (extension === "ics") {
                allFolders = [
                    ...this.calendarFolders
                ];
            }
        } else {
            allFolders = [
                ...this.messageFolders,
                ...this.documentFolders,
                ...this.calendarFolders,
                ...this.taskFolders,
                ...this.contactFolders,
                ...this.otherFolders
            ];
        }
        return allFolders;
    }

    private generateMailTree(): MailFolder[] {
        let allFolders: MailFolder[] = [];
        this.messageFolders = this.data.allFolder.filter(folder => folder.view === "message").filter( f => f.absFolderPath !== "/Chats");
        this.documentFolders = this.data.allFolder.filter(folder => folder.view === "document");
        this.contactFolders = this.data.allFolder.filter(folder => folder.view === "contact");
        this.taskFolders = this.data.allFolder.filter(folder => folder.view === "task");
        this.calendarFolders = this.data.allFolder.filter(folder => folder.view === "appointment");
        this.otherFolders = this.data.allFolder.filter(folder => !folder.view && folder.id !== "3");
        allFolders = [
            ...this.messageFolders,
            ...this.documentFolders,
            ...this.calendarFolders,
            ...this.taskFolders,
            ...this.contactFolders,
            ...this.otherFolders
        ];
        return allFolders;
    }

    selectFolder(folder) {
        this.selectedMailFolder = folder;
    }

    getParentFolder() {
        let folderId = this.selectedMailFolder.id;
        for (let i = 0; i < this.allFolders.length; i++) {
            if (this.allFolders[i].id === folderId) {
                this.selectedMailFolder = null;
            } else if (this.allFolders[i].children && this.allFolders[i].children?.length) {
                this.findAndSelectSubFolder(folderId, this.allFolders[i], i);
            }
        }
    }

    findAndSelectSubFolder(folderId: any, subFolders: any, i?: number): void {
        subFolders.children.forEach((subFolder: any, index) => {
            if (subFolder.id === folderId) {
                this.selectedMailFolder = subFolders;
            } else if (subFolder.folder) {
                this.findAndSelectSubFolder(folderId, subFolder, index);
            }
        });
    }
}
